import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cn, cva } from '@thinkalpha/ui-core/utils';
import type { VariantProps } from '@thinkalpha/ui-core/utils';
import * as React from 'react';

const rootVariants = cva(
    'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
    {
        variants: {
            size: {
                default: 'h-[calc(theme(spacing.5)+4px)] w-[calc(theme(spacing.10)+4px)]',
                sm: 'h-[calc(theme(spacing.3)+4px)] w-[calc(theme(spacing.6)+4px)]',
            },
            color: {
                default: 'data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-700',
                light: 'data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-500',
                positive: 'data-[state=checked]:bg-jade-450 data-[state=unchecked]:bg-gray-700',
            },
        },
        defaultVariants: {
            size: 'default',
            color: 'default',
        },
    },
);

const thumbVariants: typeof rootVariants = cva(
    'pointer-events-none block rounded-full shadow-lg ring-0 transition-transform',
    {
        variants: {
            size: {
                default: 'h-5 w-5 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
                sm: 'h-3 w-3 data-[state=checked]:translate-x-3 data-[state=unchecked]:translate-x-0',
            },
            color: {
                default: 'bg-background',
                light: 'bg-white',
                positive: 'bg-background',
            },
        },
        defaultVariants: {
            size: 'default',
            color: 'default',
        },
    },
);

const Switch = React.forwardRef<
    React.ElementRef<typeof SwitchPrimitives.Root>,
    React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & VariantProps<typeof rootVariants>
>(({ className, size, color, ...props }, ref) => (
    <SwitchPrimitives.Root className={cn(rootVariants({ size, color, className }))} {...props} ref={ref}>
        <SwitchPrimitives.Thumb className={cn(thumbVariants({ size, color }))} />
    </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
